import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { LogOut, Users, Eye, Lock, Loader2, Mail, Globe, FileText, ExternalLink, Clock } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "cph_admin_token";

// Turn a 2-letter ISO country code into its flag emoji.
function flagEmoji(code) {
  if (!code || code.length !== 2) return "";
  return String.fromCodePoint(
    ...code.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

// Human-friendly duration, e.g. 8 → "8s", 95 → "1m 35s".
function fmtDuration(seconds) {
  const s = Math.round(seconds || 0);
  if (s <= 0) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white rounded-3xl shadow-brut p-8 border border-zinc-200"
      >
        <div className="w-12 h-12 rounded-2xl bg-[#DF3226] flex items-center justify-center mb-6">
          <Lock className="text-white" size={22} />
        </div>
        <h1 className="font-display font-900 tracking-tight text-3xl">Admin</h1>
        <p className="font-body text-sm text-black/50 mt-1 mb-6">
          Sign in to view visitor analytics.
        </p>

        <label className="font-body font-700 text-xs uppercase tracking-wide text-black/50">
          Email
        </label>
        <input
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username"
          className="w-full mt-1 mb-4 px-4 py-3 rounded-xl border border-zinc-300 font-body text-base focus:outline-none focus:border-[#DF3226]"
        />

        <label className="font-body font-700 text-xs uppercase tracking-wide text-black/50">
          Password
        </label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          className="w-full mt-1 mb-2 px-4 py-3 rounded-xl border border-zinc-300 font-body text-base focus:outline-none focus:border-[#DF3226]"
        />

        {error && (
          <p data-testid="admin-login-error" className="font-body text-sm text-[#DF3226] mt-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="w-full mt-5 py-3 rounded-full bg-[#DF3226] text-white font-body font-700 flex items-center justify-center gap-2 hover:brightness-95 transition disabled:opacity-60"
        >
          {loading && <Loader2 className="animate-spin" size={18} />}
          Sign in
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200"
    >
      <div className="flex items-center gap-2 text-black/50">
        <Icon size={18} />
        <span className="font-body font-700 text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-display font-900 tracking-tight text-4xl mt-3">{value}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [ads, setAds] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dash, adsRes, inqRes] = await Promise.all([
        axios.get(`${API}/api/analytics/dashboard`, { headers }),
        axios.get(`${API}/api/ads/admin`, { headers }),
        axios.get(`${API}/api/ads/inquiries`, { headers }),
      ]);
      setData(dash.data);
      setAds(adsRes.data.ads || []);
      setInquiries(inqRes.data.inquiries || []);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  if (error)
    return (
      <div className="max-w-[1000px] mx-auto px-5 py-20 font-body text-[#DF3226]">{error}</div>
    );
  if (!data)
    return (
      <div className="max-w-[1000px] mx-auto px-5 py-20 flex items-center gap-2 text-black/50">
        <Loader2 className="animate-spin" size={18} /> Loading analytics…
      </div>
    );

  const chartData = data.series.map((d) => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  return (
    <div data-testid="admin-dashboard" className="max-w-[1100px] mx-auto px-5 md:px-10 py-14">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-900 tracking-tight text-4xl sm:text-5xl">
            Visitor analytics
          </h1>
          <p className="font-body text-sm text-black/50 mt-1">Last 30 days</p>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 font-body font-700 text-sm hover:border-[#DF3226] hover:text-[#DF3226] transition"
        >
          <LogOut size={16} /> Log out
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <StatCard testid="stat-today-visitors" label="Visitors today" value={data.today.visitors} icon={Users} />
        <StatCard testid="stat-today-pageviews" label="Pageviews today" value={data.today.pageviews} icon={Eye} />
        <StatCard testid="stat-total-visitors" label="Visitors · 30d" value={data.totals.visitors} icon={Users} />
        <StatCard testid="stat-total-pageviews" label="Pageviews · 30d" value={data.totals.pageviews} icon={Eye} />
        <StatCard testid="stat-avg-time" label="Avg. time on page" value={fmtDuration(data.totals.avg_seconds)} icon={Clock} />
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8">
        <h2 className="font-display font-900 tracking-tight text-lg mb-4">Daily traffic</h2>
        <div className="h-72" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={4} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="pageviews" name="Pageviews" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="visitors" name="Visitors" fill="#DF3226" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-5 mt-4 font-body text-xs text-black/60">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block bg-[#DF3226]" /> Unique visitors
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block bg-[#1E3A8A]" /> Pageviews
          </span>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8" data-testid="admin-countries">
        <div className="flex items-center gap-2 mb-1">
          <Globe size={18} className="text-[#1E3A8A]" />
          <h2 className="font-display font-900 tracking-tight text-lg">Where visitors come from</h2>
        </div>
        <p className="font-body text-sm text-black/50 mb-4">Last 30 days · by country</p>
        {(() => {
          const countries = (data.countries || []).filter((c) => c.pageviews > 0);
          if (countries.length === 0) {
            return (
              <p className="font-body text-sm text-black/40 py-6 text-center">
                No country data yet — it starts filling in as new visitors arrive.
              </p>
            );
          }
          const maxViews = Math.max(...countries.map((c) => c.pageviews), 1);
          return (
            <div className="space-y-2.5" data-testid="country-list">
              {countries.map((c) => {
                const known = c.country_code && c.country !== "Unknown";
                return (
                  <div
                    key={c.country_code || c.country}
                    data-testid={`country-row-${c.country_code || "unknown"}`}
                    className="flex items-center gap-3"
                  >
                    <span className="w-7 text-lg leading-none text-center" aria-hidden>
                      {known ? flagEmoji(c.country_code) : "🌐"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-body font-700 text-sm truncate">
                          {known ? c.country : "Unknown"}
                        </span>
                        <span className="font-body text-xs text-black/50 shrink-0">
                          {c.visitors} {c.visitors === 1 ? "visitor" : "visitors"} · {c.pageviews} views
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-zinc-100 mt-1.5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#1E3A8A]"
                          style={{ width: `${Math.max((c.pageviews / maxViews) * 100, 3)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8" data-testid="admin-pages">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={18} className="text-[#DF3226]" />
          <h2 className="font-display font-900 tracking-tight text-lg">Most viewed pages</h2>
        </div>
        <p className="font-body text-sm text-black/50 mb-4">Last 30 days · by pageviews</p>
        {(() => {
          const pages = (data.pages || []).filter((p) => p.pageviews > 0).slice(0, 15);
          if (pages.length === 0) {
            return (
              <p className="font-body text-sm text-black/40 py-6 text-center">
                No page data yet — it starts filling in as visitors browse.
              </p>
            );
          }
          const maxViews = Math.max(...pages.map((p) => p.pageviews), 1);
          return (
            <div className="space-y-2.5" data-testid="pages-list">
              {pages.map((p) => (
                <div
                  key={p.path}
                  data-testid={`page-row-${p.path}`}
                  className="flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-body font-700 text-sm truncate">
                        {p.path === "/" ? "Home" : p.path}
                      </span>
                      <span className="font-body text-xs text-black/50 shrink-0">
                        {p.visitors} {p.visitors === 1 ? "visitor" : "visitors"} · {p.pageviews} views
                        {p.avg_seconds ? ` · ${fmtDuration(p.avg_seconds)} avg` : ""}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-100 mt-1.5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#DF3226]"
                        style={{ width: `${Math.max((p.pageviews / maxViews) * 100, 3)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8" data-testid="admin-referrers">
        <div className="flex items-center gap-2 mb-1">
          <ExternalLink size={18} className="text-[#1E3A8A]" />
          <h2 className="font-display font-900 tracking-tight text-lg">Traffic sources</h2>
        </div>
        <p className="font-body text-sm text-black/50 mb-4">Last 30 days · where visitors arrived from</p>
        {(() => {
          const referrers = (data.referrers || []).filter((r) => r.visitors > 0).slice(0, 15);
          if (referrers.length === 0) {
            return (
              <p className="font-body text-sm text-black/40 py-6 text-center">
                No referrer data yet — it starts filling in as visitors arrive from other sites.
              </p>
            );
          }
          const maxVisitors = Math.max(...referrers.map((r) => r.visitors), 1);
          return (
            <div className="space-y-2.5" data-testid="referrers-list">
              {referrers.map((r) => (
                <div
                  key={r.source}
                  data-testid={`referrer-row-${r.source}`}
                  className="flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-body font-700 text-sm truncate">
                        {r.source === "Direct" ? "Direct / bookmark" : r.source}
                      </span>
                      <span className="font-body text-xs text-black/50 shrink-0">
                        {r.visitors} {r.visitors === 1 ? "visitor" : "visitors"} · {r.pageviews} views
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-100 mt-1.5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#1E3A8A]"
                        style={{ width: `${Math.max((r.visitors / maxVisitors) * 100, 3)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8" data-testid="admin-ads">
        <h2 className="font-display font-900 tracking-tight text-lg mb-1">Advertisers</h2>
        <p className="font-body text-sm text-black/50 mb-4">{ads.length} total · watch the expiry dates for renewals</p>
        {ads.length === 0 ? (
          <p className="font-body text-sm text-black/40 py-6 text-center">No advertisers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="font-body font-800 text-[11px] uppercase tracking-wide text-black/40 border-b border-zinc-200">
                  <th className="py-2 pr-4">Business</th>
                  <th className="py-2 pr-4">Area</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Contact</th>
                </tr>
              </thead>
              <tbody className="font-body text-sm">
                {ads.map((ad) => {
                  const exp = ad.expires_at ? ad.expires_at.slice(0, 10) : "—";
                  const soon =
                    ad.expires_at && new Date(ad.expires_at) - Date.now() < 30 * 864e5 && ad.status === "active";
                  return (
                    <tr key={ad.id} data-testid={`admin-ad-${ad.id}`} className="border-b border-zinc-100">
                      <td className="py-2.5 pr-4 font-700">{ad.business_name}</td>
                      <td className="py-2.5 pr-4">{ad.area}</td>
                      <td className="py-2.5 pr-4">{ad.tier}{ad.addon_website ? " +web" : ""}</td>
                      <td className="py-2.5 pr-4">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[11px] font-800"
                          style={
                            ad.status === "active"
                              ? { background: "#DCFCE7", color: "#166534" }
                              : { background: "#FEF3C7", color: "#92400E" }
                          }
                        >
                          {ad.status}
                        </span>
                      </td>
                      <td className={`py-2.5 pr-4 ${soon ? "text-[#DF3226] font-800" : ""}`}>{exp}{soon ? " ⚠" : ""}</td>
                      <td className="py-2.5 pr-4 text-black/60">
                        {ad.contact_name} · {ad.email}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl shadow-brut p-6 border border-zinc-200 mt-8" data-testid="admin-inquiries">
        <h2 className="font-display font-900 tracking-tight text-lg mb-1 flex items-center gap-2">
          <Mail size={18} /> Advertising enquiries
        </h2>
        <p className="font-body text-sm text-black/50 mb-4">{inquiries.length} total · leads from the advertising page</p>
        {inquiries.length === 0 ? (
          <p className="font-body text-sm text-black/40 py-6 text-center">No enquiries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="font-body font-800 text-[11px] uppercase tracking-wide text-black/40 border-b border-zinc-200">
                  <th className="py-2 pr-4">Received</th>
                  <th className="py-2 pr-4">Business</th>
                  <th className="py-2 pr-4">Package</th>
                  <th className="py-2 pr-4">Area</th>
                  <th className="py-2 pr-4">Contact</th>
                  <th className="py-2 pr-4">Phone</th>
                </tr>
              </thead>
              <tbody className="font-body text-sm">
                {inquiries.map((q) => (
                  <tr key={q.id} data-testid={`admin-inquiry-${q.id}`} className="border-b border-zinc-100">
                    <td className="py-2.5 pr-4 text-black/50 whitespace-nowrap">{q.created_at ? q.created_at.slice(0, 10) : "—"}</td>
                    <td className="py-2.5 pr-4 font-700">
                      {q.business_name}
                      {q.website ? (
                        <a href={q.website} target="_blank" rel="noopener noreferrer" className="block text-[11px] font-500 text-[#1E3A8A] truncate max-w-[180px]">{q.website}</a>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-4">{q.package || "—"}{q.addon_website ? " +web" : ""}</td>
                    <td className="py-2.5 pr-4">{q.area || "—"}</td>
                    <td className="py-2.5 pr-4 text-black/60">
                      {q.contact_name} · <a href={`mailto:${q.email}`} className="text-[#DF3226]">{q.email}</a>
                    </td>
                    <td className="py-2.5 pr-4 text-black/60">{q.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  return (
    <main className="min-h-screen bg-[#F9F8F6]">
      <Seo
        title="Admin · CPH Highlights"
        description="Admin dashboard"
        noindex
        path="/admin"
      />
      {token ? (
        <Dashboard token={token} onLogout={logout} />
      ) : (
        <LoginForm onSuccess={setToken} />
      )}
    </main>
  );
}
